import { GET_TRENDING_EVENTS } from '~/graphql/queries/getTrendingEvents'
import { GET_SEARCH_RESULTS } from '~/graphql/queries/getSearchResults'
import {
  ChevronLeftAlt,
  CloseRounded,
  Event,
  Location,
  MagnifyingGlass,
} from '@ticketswap/solar/icons'
import {
  Avatar,
  BaseButton,
  Button,
  color,
  device,
  H3,
  H4,
  H6,
  HorizontalScroll,
  InputV2 as Input,
  radius,
  shadow,
  space,
  Spinner,
} from '@ticketswap/solar'
import Link from 'next/link'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import useLanguage from '~/hooks/useLanguage'
import { useTranslation } from '~/components/TranslationContext'
import { createEventDateFormatter } from '~/utils/date/createEventDateFormatter'
import { useQuery } from '@apollo/client'
import { RouteMap } from '~/lib/routeMap'
import { useState } from 'react'

const Menu = styled.div`
  text-align: start;
  overflow: hidden;
  background-color: ${color.background};

  @media ${device.tablet} {
    position: absolute;
    inset-inline: 0;
    inset-block-start: calc(100% + ${space[4]});
    padding-block: 0;
    padding-inline: 0;
    border: 0;
    border-radius: ${radius.md};
    box-shadow: ${shadow.strong};

    [data-theme='dark'] & {
      border: 1px solid ${color.stroke};
    }
  }
`

const MenuBody = styled.div`
  padding-block: ${space[16]};
  padding-inline: ${space[16]};
  display: grid;
  grid-gap: ${space[16]};
  border-block-start: ${props => (props.isOpen ? `1px solid ${color.stroke}` : 0)};

  @media ${device.tablet} {
    border: 0;
    padding-block: ${space[16]} ${space[24]};
    padding-inline: ${space[24]};
  }
`

const MenuFooter = styled.div`
  border-block-start: 1px solid ${color.stroke};
`

const ShowMoreLink = styled.a`
  text-align: center;
  height: ${space[56]};
  display: flex;
  justify-content: center;
  align-items: center;

  color: ${color.action};
  cursor: pointer;

  &:hover,
  &:focus {
    color: ${color.foreground};
  }
`

const ItemContainer = styled.div`
  position: relative;
  color: ${props => (props.highlighted ? color.foreground : color.action)};
  padding-inline-start: ${40 / 16}rem;
  cursor: pointer;

  h4 {
    color: ${props => (props.highlighted ? color.foreground : color.action)};
  }
`

const ItemAdornment = styled.span`
  position: absolute;
  inset-block-start: 0;
  inset-inline-start: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  transform: translate3d(-3px, -1px, 0);

  @media ${device.tablet} {
    transform: translate3d(-3px, 2px, 0);
  }
`

const ItemDetails = styled.span`
  color: ${color.foregroundMuted};
`

const Item = ({
  node: { __typename: type, ...node },
  t,
  highlighted,
  'aria-selected': ariaSelected,
  id,
  onClick,
  onMouseDown,
  onMouseMove,
  role,
  selected,
}) => {
  const language = useLanguage()
  const formatEventDate = createEventDateFormatter(language)
  const eventTime = formatEventDate(node.startDate, node.endDate)
  let artistInfo = t('common:search.artist')
  let venueInfo = t('common:search.venue')
  let cityInfo = t('common:search.city')
  let eventInfo = eventTime

  if (node.name) {
    cityInfo = `${cityInfo} • ${node.name}`
  }
  if (node.locationName) {
    eventInfo = node.hasOngoingEventType
      ? `${node.locationName}`
      : `${eventInfo} • ${node.locationName}`
  }
  if (node.cityName) {
    venueInfo = `${venueInfo} • ${node.cityName}`
    eventInfo = `${eventInfo}, ${node.cityName}`
  }
  if (node.country && node.country.code) {
    venueInfo = `${venueInfo}, ${node.country.code}`
    cityInfo = `${cityInfo}, ${node.country.code}`
    eventInfo = `${eventInfo}, ${node.country.code}`
  }

  return (
    <ItemContainer
      highlighted={highlighted}
      ariaSelected={ariaSelected}
      id={id}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      role={role}
      selected={selected}
    >
      <ItemAdornment>
        {type === 'LocationResult' || type === 'CityResult' ? (
          <Location size={24} />
        ) : type === 'ArtistResult' ? (
          <Avatar src={node.avatar} size={24} />
        ) : (
          <Event size={24} />
        )}
      </ItemAdornment>
      <H4>{node.name}</H4>
      <ItemDetails>
        {type === 'ArtistResult'
          ? artistInfo
          : type === 'LocationResult'
          ? venueInfo
          : type === 'CityResult'
          ? cityInfo
          : eventInfo}
      </ItemDetails>
    </ItemContainer>
  )
}

const NothingFoundContainer = styled.div`
  text-align: center;
  padding-block: ${space[16]};
  padding-inline: ${space[16]};
  color: ${color.foreground};
`

const NothingFoundInfo = styled.p`
  margin-block-start: ${space[4]};
  margin-block-end: ${space[16]};
  color: ${color.foregroundMuted};
`

const NothingFound = ({ t, query, closeSearch }) => (
  <NothingFoundContainer>
    <H3>{t('common:search.nothingFound')}</H3>
    <NothingFoundInfo>{t('common:search.info')}</NothingFoundInfo>
    <Link
      prefetch={false}
      href={{ pathname: RouteMap['ADD_EVENT'], query: { title: query } }}
      passHref
    >
      <Button as="a" leftAdornment={<Event size={24} />} onClick={closeSearch}>
        {t('common:search.addNew')}
      </Button>
    </Link>
  </NothingFoundContainer>
)

const Container = styled.div`
  position: ${props => (props.isOpen ? 'fixed' : 'relative')};
  margin-block: 0;
  margin-inline: auto;
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;

  ${props =>
    props.isOpen &&
    css`
      inset: 0;
      background-color: ${color.background};
      z-index: 2147483647;
    `};

  @media ${device.tablet} {
    overflow-y: visible;
    max-width: ${props => (props.width === 'medium' ? `${640 / 16}rem` : 'none')};
    position: relative;
    inset: auto;
    background-color: transparent;
  }
`

const InputWrapper = styled.div`
  position: relative;
  padding-block-start: ${props => (props.isOpen ? space[16] : 0)};
  padding-block-end: ${props => (props.isOpen ? space[16] : 0)};
  padding-inline-end: ${props => (props.isOpen ? space[16] : 0)};
  padding-inline-start: ${props => (props.isOpen ? space[56] : 0)};
  display: flex;
  flex-direction: row;
  align-items: center;
  align-content: stretch;

  > * {
    flex-basis: 100%;
  }

  @media ${device.tablet} {
    padding-block: 0;
    padding-inline: 0;
  }
`

const ToggleButton = styled.button`
  position: absolute;
  inset-inline-start: 0;
  color: ${color.foregroundMuted};
  padding-block: ${space[12]};
  padding-inline: ${space[12]};
  outline: 0;

  @media ${device.tablet} {
    display: none;
  }

  &:hover,
  &:focus {
    color: ${color.foreground};
  }
`

const FilterWrapper = styled.div`
  margin-block-end: ${space[16]};

  @media ${device.tablet} {
    display: none;
  }
`

const StyledH6 = styled(H6)`
  padding-block-start: ${space[4]};

  @media ${device.tablet} {
    padding-block-start: ${space[8]};
  }
`

const SearchResults = ({
  isOpen,
  width,
  withRecentlyVisited,
  query,
  resultsPerPage,
  filter,
  trackSearchResultsDebounced,
  loggedInUser,
  recentlyVisitedEvents,
  inputValue,
  getToggleButtonProps,
  getInputProps,
  defaultOpen,
  getLabelProps,
  openMenu,
  clearSelection,
  getMenuProps,
  setFilter,
  onClose,
  getItemProps,
  highlightedIndex,
  selectedItem,
  geoInfo,
}) => {
  const { t } = useTranslation()
  const [results, setResults] = useState([])
  const [pageInfo, setPageInfo] = useState({})
  const { data: trendingData = {}, loading: loadingEvents } = useQuery(GET_TRENDING_EVENTS, {
    skip: !isOpen || withRecentlyVisited,
    variables: geoInfo,
  })

  const { nearby } = trendingData
  const { loading: loadingSearchResults } = useQuery(GET_SEARCH_RESULTS, {
    skip: !isOpen || query.length === 0,
    variables: {
      query,
      first: resultsPerPage || 5,
      type: filter,
    },
    onCompleted: data => {
      setResults(data?.search?.edges || [])
      setPageInfo(data?.search?.pageInfo || {})

      if (query) {
        trackSearchResultsDebounced(data?.search?.edges.length > 0)
      }
    },
  })

  const trendingEvents = (nearby && nearby.trendingEvents) || []
  const hasNextPage = Boolean(pageInfo.hasNextPage)
  const showSearchResults =
    (!loadingSearchResults && results && query.length > 0) ||
    (loadingSearchResults && results.length > 0)
  const showTrending =
    !loggedInUser && !showSearchResults && trendingEvents && trendingEvents.length > 0
  const showRecentlyVisited =
    loggedInUser && !showSearchResults && recentlyVisitedEvents && recentlyVisitedEvents.length > 0
  const items = [
    ...(showSearchResults ? results : []),
    ...(showTrending ? trendingEvents : []),
    ...(showRecentlyVisited ? recentlyVisitedEvents : []),
  ]
  const encodedInputValue = encodeURIComponent(inputValue)
  const showMenu = isOpen && (showSearchResults || showRecentlyVisited || showTrending)
  const heading = showSearchResults
    ? 'common:search.results'
    : showRecentlyVisited
    ? 'common:search.recentlyVisited'
    : 'common:search.trending'

  return (
    <Container isOpen={isOpen} width={width}>
      <label htmlFor="a11y-input" aria-hidden="true" style={{ display: 'none' }}>
        <input type="text" id="a11y-input" tabIndex="-1" />
      </label>
      <InputWrapper isOpen={isOpen}>
        {isOpen && (
          <ToggleButton {...getToggleButtonProps()}>
            <ChevronLeftAlt />
          </ToggleButton>
        )}
        <Input
          {...getInputProps({
            'aria-label': t('common:search.label'),
            labelProps: getLabelProps(),
            type: 'text',
            placeholder: t('common:search.info'),
            autoFocus: defaultOpen,
            onFocus: openMenu,
            startAdornment: <MagnifyingGlass size={24} />,
            endAdornment:
              ((loadingEvents || loadingSearchResults) && <Spinner />) ||
              (query && (
                <BaseButton onClick={clearSelection}>
                  <CloseRounded color={color.foregroundSubtle} size={24} />
                </BaseButton>
              )),
          })}
        />
      </InputWrapper>
      {showMenu && (
        <Menu {...getMenuProps()}>
          {showSearchResults && (
            <FilterWrapper>
              <HorizontalScroll>
                <Button
                  size="medium"
                  variant="secondary"
                  active={!filter}
                  onClick={() => setFilter(null)}
                >
                  {t('common:search.allResults')}
                </Button>
                <Button
                  size="medium"
                  variant="secondary"
                  active={filter === 'EVENT'}
                  onClick={() => setFilter('EVENT')}
                >
                  {t('common:search.event_plural')}
                </Button>
                <Button
                  size="medium"
                  variant="secondary"
                  active={filter === 'LOCATION'}
                  onClick={() => setFilter('LOCATION')}
                >
                  {t('common:search.venue_plural')}
                </Button>
                <Button
                  size="medium"
                  variant="secondary"
                  active={filter === 'CITY'}
                  onClick={() => setFilter('CITY')}
                >
                  {t('common:search.city_plural')}
                </Button>
                <Button
                  size="medium"
                  variant="secondary"
                  active={filter === 'ARTIST'}
                  onClick={() => setFilter('ARTIST')}
                >
                  {t('common:search.artist_plural')}
                </Button>
              </HorizontalScroll>
            </FilterWrapper>
          )}

          <MenuBody isOpen={isOpen}>
            {!(showSearchResults && results.length === 0) && <StyledH6>{t(heading)}</StyledH6>}

            {showSearchResults && items.length === 0 ? (
              <NothingFound t={t} query={query} closeSearch={onClose} />
            ) : null}

            {items.length > 0 &&
              items.map((el, index) => {
                const node = el.node ? el.node : el

                return (
                  // eslint-disable-next-line react/jsx-key
                  <Item
                    {...getItemProps({
                      key: node.id,
                      index,
                      item: node,
                      node,
                      t,
                      highlighted: highlightedIndex === index ? 'true' : undefined,
                      selected: selectedItem === index ? 'true' : undefined,
                    })}
                  />
                )
              })}
          </MenuBody>

          {showSearchResults && hasNextPage ? (
            <MenuFooter>
              <Link
                prefetch={false}
                href={{
                  pathname: RouteMap['SEARCH'],
                  query: {
                    query: encodedInputValue,
                  },
                }}
                passHref
              >
                <ShowMoreLink>{t('common:search.showMore')}</ShowMoreLink>
              </Link>
            </MenuFooter>
          ) : null}
        </Menu>
      )}
    </Container>
  )
}

export default SearchResults
